// src/hooks/useImpactedModules.js
// Custom hook that fetches and caches impacted module names from the Nocobase API.
// Uses a module-level cache so the API is called only once per page load.
// Falls back to the static IMPACTED_MODULES list if the API call fails.

import { useState, useEffect } from 'react';
import { fetchImpactedModules } from '../services/api';
import { IMPACTED_MODULES } from '../data/formOptions';

// Module-level cache — survives re-renders and step navigation
let _cachedModules = null;
let _fetchPromise = null;

/**
 * Resets the module-level cache. Intended for testing only.
 */
export function _resetCache() {
  _cachedModules = null;
  _fetchPromise = null;
}

/**
 * Fetches and returns the list of impacted modules from the Nocobase API.
 * Results are cached at the module level so the API is called at most once per page load.
 * On failure, falls back to the static IMPACTED_MODULES list from formOptions.
 *
 * @returns {{ modules: string[], isLoading: boolean, error: string | null }}
 */
export function useImpactedModules() {
  const [modules, setModules] = useState(_cachedModules ?? []);
  const [isLoading, setIsLoading] = useState(_cachedModules === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_cachedModules) {
      setModules(_cachedModules);
      setIsLoading(false);
      return;
    }

    // Deduplicate concurrent calls — share the same in-flight promise
    if (!_fetchPromise) {
      _fetchPromise = fetchImpactedModules()
        .then((data) => {
          _cachedModules = data.length > 0 ? data : IMPACTED_MODULES;
          return _cachedModules;
        })
        .catch((err) => {
          _fetchPromise = null; // allow retry on next render
          // Fall back to static list on failure
          _cachedModules = IMPACTED_MODULES;
          return _cachedModules;
        });
    }

    _fetchPromise
      .then((parsed) => {
        setModules(parsed);
        setError(null);
      })
      .catch((err) => {
        setError(err.message ?? 'Failed to load impacted modules');
        setModules(IMPACTED_MODULES);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return { modules, isLoading, error };
}
